"use client";

import { useState } from "react";
import Link from "next/link";
import { Brain, ChevronLeft, CheckCircle, X, ArrowRight } from "lucide-react";
import { ThemeToggle } from "@/lib/theme";
import { DIAGNOSTIC_WORDS } from "@/lib/vocab/sampleData";

type WordState = "unknown" | "known" | null;

export default function DiagnosticPage() {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState<WordState[]>(Array(DIAGNOSTIC_WORDS.length).fill(null));
  const [showDef, setShowDef] = useState(false);
  const [done, setDone] = useState(false);

  const word = DIAGNOSTIC_WORDS[current];
  const answered = answers.filter(a => a !== null).length;
  const inPool = answers.filter(a => a === "unknown").length;
  const progress = (current / DIAGNOSTIC_WORDS.length) * 100;

  function handleAnswer(state: "known" | "unknown") {
    const next = [...answers];
    next[current] = state;
    setAnswers(next);
    setShowDef(true);
    setTimeout(() => {
      setShowDef(false);
      if (current + 1 >= DIAGNOSTIC_WORDS.length) {
        setDone(true);
      } else {
        setCurrent(c => c + 1);
      }
    }, 900);
  }

  if (done) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: "var(--bg-primary)" }}>
        <nav className="border-b" style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-card)" }}>
          <div className="max-w-xl mx-auto px-4 h-14 flex items-center justify-between">
            <Link href="/vocab" className="flex items-center gap-2 text-sm" style={{ color: "var(--text-muted)" }}>
              <Brain size={16} className="text-blue-500" /> VocabMaster
            </Link>
            <ThemeToggle />
          </div>
        </nav>
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="w-full max-w-md text-center">
            <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2" style={{ color: "var(--text-primary)" }}>Diagnostic Complete!</h1>
            <p className="mb-6" style={{ color: "var(--text-muted)" }}>
              You reviewed <strong>{answered}</strong> words and added <strong className="text-blue-600">{inPool}</strong> to your learning pool.
            </p>

            <div className="rounded-2xl border p-6 mb-6 text-left" style={{ backgroundColor: "var(--bg-card)", borderColor: "var(--border)" }}>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-blue-600">{answered}</div>
                  <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>Reviewed</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">{answers.filter(a => a === "known").length}</div>
                  <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>Already knew</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-purple-600">{inPool}</div>
                  <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>Added to pool</div>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <Link href="/vocab/study"
                className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2">
                Study These Words Now <ArrowRight size={16} />
              </Link>
              <Link href="/vocab/pool"
                className="w-full py-3 rounded-xl font-medium text-sm hover:opacity-80 transition-opacity flex items-center justify-center gap-2"
                style={{ backgroundColor: "var(--bg-secondary)", color: "var(--text-secondary)" }}>
                View My Pool
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "var(--bg-primary)" }}>
      {/* Nav */}
      <nav className="border-b" style={{ borderColor: "var(--border)", backgroundColor: "var(--bg-card)" }}>
        <div className="max-w-xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link href="/vocab" className="flex items-center gap-2 text-sm" style={{ color: "var(--text-muted)" }}>
            <ChevronLeft size={16} /> Back
          </Link>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium" style={{ color: "var(--text-muted)" }}>
              {current + 1} / {DIAGNOSTIC_WORDS.length}
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
              {inPool} in pool
            </span>
          </div>
          <ThemeToggle />
        </div>
      </nav>

      {/* Progress bar */}
      <div className="h-1" style={{ backgroundColor: "var(--bg-secondary)" }}>
        <div className="h-1 bg-blue-500 transition-all duration-300" style={{ width: `${progress}%` }} />
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-md">

          {/* Header */}
          <p className="text-center text-sm mb-8" style={{ color: "var(--text-muted)" }}>
            TOEFL Academic 500 · Diagnostic
          </p>

          {/* Word card */}
          <div className={`rounded-2xl border p-10 text-center mb-6 transition-all duration-200 ${showDef ? "scale-95 opacity-70" : "scale-100 opacity-100"}`}
            style={{ backgroundColor: "var(--bg-card)", borderColor: "var(--border)" }}>
            <div className="text-4xl font-bold mb-3 tracking-tight" style={{ color: "var(--text-primary)" }}>
              {word.word}
            </div>
            {showDef ? (
              <p className="text-sm italic" style={{ color: "var(--text-secondary)" }}>{word.definition}</p>
            ) : (
              <p className="text-sm" style={{ color: "var(--text-faint)" }}>Do you know this word?</p>
            )}
          </div>

          {/* Buttons */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <button
              onClick={() => handleAnswer("unknown")}
              disabled={showDef}
              className="py-4 rounded-xl font-semibold text-sm border-2 border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors disabled:opacity-40"
            >
              + Add to Pool
            </button>
            <button
              onClick={() => handleAnswer("known")}
              disabled={showDef}
              className="py-4 rounded-xl font-semibold text-sm border-2 border-green-200 bg-green-50 text-green-700 hover:bg-green-100 transition-colors disabled:opacity-40"
            >
              I know this ✓
            </button>
          </div>

          {/* Skip */}
          <div className="flex items-center justify-center gap-4 text-sm" style={{ color: "var(--text-muted)" }}>
            <button
              onClick={() => { if (current > 0) { setCurrent(c => c - 1); setShowDef(false); } }}
              className="flex items-center gap-1 hover:opacity-70 transition-opacity"
            >
              <ChevronLeft size={14} /> Previous
            </button>
            <span style={{ color: "var(--border)" }}>·</span>
            <button
              onClick={() => { if (current + 1 < DIAGNOSTIC_WORDS.length) { setCurrent(c => c + 1); setShowDef(false); } else setDone(true); }}
              className="flex items-center gap-1 hover:opacity-70 transition-opacity"
            >
              Skip <X size={12} />
            </button>
          </div>

          {/* Word grid preview */}
          <div className="mt-10 flex flex-wrap gap-1.5 justify-center">
            {DIAGNOSTIC_WORDS.map((w, i) => (
              <div key={w.word}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === current ? "bg-blue-500 scale-150" :
                  answers[i] === "unknown" ? "bg-purple-400" :
                  answers[i] === "known" ? "bg-green-400" :
                  "bg-gray-200"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
